"use client";

import { useEffect, useRef, useState } from "react";
import { useTraceStore } from "@/store/useTraceStore";
import { ANIMATION, SPEED_MULTIPLIERS } from "@/config/constants";

/**
 * Drives the hop-by-hop animation sequence.
 *
 * Reads phase, activeHopIndex, hops, and speedMode from the Zustand store.
 * Advances activeHopIndex on a timer, sets camera modes, and highlights
 * active submarine cables as the animation progresses.
 *
 * Pauses when the tab is hidden or when sharing is in progress.
 *
 * @param cableNameMapRef  Lookup from lowercase cable name to feature IDs
 * @param cablesLoadedRef  Whether cable data has finished loading
 */
export function useAnimationSequencer(
  cableNameMapRef: React.RefObject<Map<string, string[]>>,
  cablesLoadedRef: React.RefObject<boolean>
) {
  const phase = useTraceStore((s) => s.phase);
  const hops = useTraceStore((s) => s.hops);
  const activeHopIndex = useTraceStore((s) => s.activeHopIndex);
  const speedMode = useTraceStore((s) => s.speedMode);
  const setPhase = useTraceStore((s) => s.setPhase);
  const setCameraMode = useTraceStore((s) => s.setCameraMode);
  const setActiveHopIndex = useTraceStore((s) => s.setActiveHopIndex);
  const setActiveCableIds = useTraceStore((s) => s.setActiveCableIds);

  // Store speedMode in a ref so the effect doesn't re-fire when speed changes mid-hop
  const speedModeRef = useRef(speedMode);
  useEffect(() => {
    speedModeRef.current = speedMode;
  }, [speedMode]);

  // Track tab visibility — pause animation when tab is hidden
  const [tabVisible, setTabVisible] = useState(true);
  useEffect(() => {
    const handleVisibility = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    // Don't advance if tab is hidden or not in animating phase
    if (phase !== "animating" || activeHopIndex < 0 || !tabVisible) return;
    if (activeHopIndex >= hops.length) {
      setPhase("complete");
      setCameraMode("reveal");
      return;
    }

    const hop = hops[activeHopIndex];

    // Set camera mode based on hop type
    if (hop.cable) {
      setCameraMode("whale");
      // Find cable featureIds by searching the name lookup map
      if (cablesLoadedRef.current) {
        const cableName = hop.cable.name.toLowerCase();
        const nameMap = cableNameMapRef.current;
        let matchedIds: string[] = [];
        // Use exact-match only. Substring matching (key.includes / cableName.includes)
        // causes false positives: "aae-1" would match "aae-1 extension", highlighting
        // the wrong cable segment on the globe.
        const exactMatch = nameMap.get(cableName);
        if (exactMatch) {
          matchedIds = exactMatch;
        }
        setActiveCableIds(matchedIds);
      }
    } else if (activeHopIndex === 0) {
      setCameraMode("departure");
    } else if (activeHopIndex === hops.length - 1) {
      setCameraMode("arrival");
    } else {
      setCameraMode("follow");
    }

    // Use ref for speed so mid-animation changes don't restart the timer
    const currentSpeed = speedModeRef.current;
    const baseDelay = hop.cable
      ? ANIMATION.CABLE_HOP_DELAY
      : hop.latency < 10
        ? ANIMATION.FAST_HOP_DELAY
        : ANIMATION.NORMAL_HOP_DELAY;
    const delay = baseDelay * (SPEED_MULTIPLIERS[currentSpeed] ?? 1);

    const timer = setTimeout(() => {
      setActiveHopIndex(activeHopIndex + 1);
    }, delay);

    return () => clearTimeout(timer);
  }, [
    phase,
    activeHopIndex,
    hops,
    tabVisible,
    setPhase,
    setCameraMode,
    setActiveHopIndex,
    setActiveCableIds,
    cableNameMapRef,
    cablesLoadedRef,
  ]);
}
